import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useMemo } from 'react';

import BackendService from '../services/backendService';
import { Template } from '../services/TemplateService/Template.type';
import useAutosaveTemplate from './useAutosaveTemplate';
import { DesignerUIStateActions, useDesignerUIContext } from './useDesignerUIState';
import useMe from './useMe';

export const useRawTemplate = (
  templateId?: string | null,
  isFromDiscover?: boolean | null,
  templateTenantId?: string,
) => {
  const templateCacheKey = ['template', templateId];
  const dispatch = useDesignerUIContext(context => context.dispatch);

  const { data: template, isFetching: isFetchingTemplate, isError, error } = useQuery<
    Template | undefined,
    AxiosError
  >(
    templateCacheKey,
    async _ => {
      if (templateId) {
        if (isFromDiscover) {
          const indexedTemplate = await BackendService.instance.discover.getIndexedTemplate(
            templateId,
          );
          return {
            id: indexedTemplate.id,
            createdAt: indexedTemplate.createdAt,
            modifiedAt: indexedTemplate.modifiedAt,
            tenantId: indexedTemplate.tenantId,
            title: indexedTemplate.title,
            type: 'template',
            creator: { uid: indexedTemplate.creatorId },
            members: [],
            isPrivate: false,
            parentId: null,
            content: {
              rounds: [],
              gameIds: [],
              slides: indexedTemplate.slides,
            },
          } as Template;
        }
        if (templateTenantId) {
          return BackendService.instance.blueprint.retrieveTemplateAuthenticated(
            templateId,
            templateTenantId,
          ) as Promise<Template | undefined>;
        }
        return BackendService.instance.blueprint.retrieveTemplate(templateId) as Promise<
          Template | undefined
        >;
      }
      return Promise.resolve(undefined);
    },
    {
      enabled: Boolean(templateId),
      staleTime: 1000 * 60 * 5,
      onSuccess: () => {
        dispatch({
          type: DesignerUIStateActions.setForceLoadingState,
          payload: 'success',
        });
      },
    },
  );
  return {
    template,
    isFetchingTemplate,
    isError,
    error,
  };
};

// TODO: make the params into useTemplate object instead of args
function useTemplate(
  templateId?: string | null,
  isFromDiscover?: boolean | null,
  templateTenantId?: string,
) {
  const { me } = useMe();

  const { error, isError, isFetchingTemplate, template } = useRawTemplate(
    templateId,
    isFromDiscover,
    templateTenantId,
  );

  const isTemplateOwner = useMemo(
    () => template && !isFromDiscover && me?.uid === template?.creator.uid,
    [isFromDiscover, me, template],
  );

  const {
    autoSaveTemplate,
    isSavingTemplate,
    isSavingTemplateError,
    isSavingTemplateSuccess,
    isSavingTemplateIdle,
  } = useAutosaveTemplate();

  return {
    template,
    isFetchingTemplate,
    autoSaveTemplate,
    isSavingTemplate,
    isError,
    error,
    isTemplateOwner,
    isSavingTemplateError,
    isSavingTemplateSuccess,
    isSavingTemplateIdle,
  };
}
export default useTemplate;
